import React, { useState } from 'react';

/**
 * Drag-and-drop question: match items to targets.
 * Uses tap-to-select on mobile (simpler and more reliable than HTML5 DnD on touch).
 */
export default function DragDrop({ question, answer = {}, onChange, disabled = false }) {
  const [selectedItem, setSelectedItem] = useState(null);

  const items = question.drag_items || [];
  const targets = question.drop_targets || [];

  // Items that haven't been placed yet
  const placedItems = new Set(Object.values(answer));
  const unplacedItems = items.filter(item => !placedItems.has(item.id));

  const handleItemClick = (itemId) => {
    if (disabled) return;
    setSelectedItem(selectedItem === itemId ? null : itemId);
  };

  const handleTargetClick = (targetId) => {
    if (disabled || !selectedItem) return;

    const newAnswer = { ...answer };

    // Remove item from any previous target
    for (const [key, val] of Object.entries(newAnswer)) {
      if (val === selectedItem) delete newAnswer[key];
    }

    // Place in new target
    newAnswer[targetId] = selectedItem;
    onChange(newAnswer);
    setSelectedItem(null);
  };

  const handleReset = () => {
    onChange({});
    setSelectedItem(null);
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        Tap an item, then tap its target to place it.
      </p>

      {/* Available items */}
      <div className="mb-4">
        <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Items</div>
        <div className="flex flex-wrap gap-2">
          {unplacedItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleItemClick(item.id)}
              disabled={disabled}
              className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                selectedItem === item.id
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
            >
              {item.text}
            </button>
          ))}
          {unplacedItems.length === 0 && !disabled && (
            <span className="text-sm text-gray-400 italic">All items placed</span>
          )}
        </div>
      </div>

      {/* Drop targets */}
      <div className="space-y-2">
        <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Targets</div>
        {targets.map(target => {
          const placedItemId = answer[target.id];
          const placedItem = items.find(i => i.id === placedItemId);

          return (
            <div
              key={target.id}
              onClick={() => handleTargetClick(target.id)}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                selectedItem
                  ? 'border-blue-300 bg-blue-50/50 cursor-pointer'
                  : 'border-gray-200 bg-gray-50'
              } ${disabled ? 'cursor-default' : ''}`}
            >
              <span className="text-gray-600 font-medium min-w-[120px]">{target.label}</span>
              <span className="text-gray-300 mx-2">→</span>
              {placedItem ? (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                  {placedItem.text}
                </span>
              ) : (
                <span className="px-3 py-1 bg-gray-100 text-gray-400 rounded text-sm italic border border-dashed border-gray-300">
                  Drop here
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Reset button */}
      {!disabled && Object.keys(answer).length > 0 && (
        <button
          onClick={handleReset}
          className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Reset all
        </button>
      )}
    </div>
  );
}
